import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Play, Pause, SkipForward, X as XIcon, Camera, Check, CurrencyDollar,
  Package, Truck, Wrench, Bed, WarningCircle, ChatCircleDots, Sparkle,
  ArrowRight, ArrowLeft, DownloadSimple, Bell, CheckCircle, Checks,
} from "@phosphor-icons/react";
import { LogoQuilla } from "../components/Logo";

// -------- Guión del tour (8 escenas · ~60s totales) --------
const SCENES = [
  {
    id: "intro",
    role: "sistema",
    hi_before: 82,
    hi_after: 82,
    duration_ms: 5000,
    caption: "Son las 12:14 pm en Ocean El Faro. En Hab. 320 acaba de fallar el aire. Nadie lo sabe todavía.",
  },
  {
    id: "maria",
    role: "María · Housekeeping",
    hi_before: 82,
    hi_after: 76,
    duration_ms: 9000,
    caption: "María entra a limpiar y detecta el problema. Reporta con foto en menos de 30 segundos.",
  },
  {
    id: "jose_valida",
    role: "José · Mantenimiento",
    hi_before: 76,
    hi_after: 76,
    duration_ms: 8000,
    caption: "José recibe el reporte con el costo del compresor ya asignado. Diagnostica: requiere repuesto.",
  },
  {
    id: "gerencia",
    role: "Gerencia",
    hi_before: 76,
    hi_after: 76,
    duration_ms: 8000,
    caption: "Gerencia ve exactamente hacia dónde va la orden antes de aprobar. Un toque y avanza.",
  },
  {
    id: "pedro",
    role: "Pedro · Compras",
    hi_before: 76,
    hi_after: 68,
    duration_ms: 10000,
    caption: "El proveedor autorizado se sugiere solo. Pedro confirma la compra y QUILLA emite el pedido en PDF.",
  },
  {
    id: "espera",
    role: "sistema",
    hi_before: 68,
    hi_after: 68,
    duration_ms: 5000,
    caption: "Cronómetro visible. Tres días de espera del repuesto, medidos y auditados.",
  },
  {
    id: "cierre",
    role: "José · Mantenimiento",
    hi_before: 68,
    hi_after: 82,
    duration_ms: 7000,
    caption: "Repuesto recibido. José ejecuta y cierra. La habitación vuelve al inventario.",
  },
  {
    id: "final",
    role: "QUILLA",
    hi_before: 82,
    hi_after: 82,
    duration_ms: 8000,
    caption: "Ciclo cerrado, Health Index recuperado, todo auditado. Sin llamadas, sin WhatsApp, sin planillas.",
  },
];

const TOTAL_MS = SCENES.reduce((a, s) => a + s.duration_ms, 0);

// -------- Utilidades visuales --------
function HealthBar({ score }) {
  const level = score >= 80 ? "bajo" : score >= 60 ? "medio" : "alto";
  const color = score >= 80 ? "#10b981" : score >= 60 ? "#C9A227" : "#ef4444";
  const label = { bajo: "RIESGO BAJO", medio: "RIESGO MEDIO", alto: "RIESGO ALTO" }[level];
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-baseline justify-between mb-2">
        <div className="q-label text-white/60">Quilla Health Index</div>
        <div className="text-[10px] font-mono px-2 py-0.5 rounded" style={{ backgroundColor: color + "33", color }}>{label}</div>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-5xl font-light tabular-nums" style={{ color }} data-testid="demo-hi-score">{score}</div>
        <div className="text-white/40 text-sm">/ 100</div>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full transition-all duration-1000 ease-out" style={{ width: `${score}%`, backgroundColor: color }}/>
      </div>
    </div>
  );
}

function PhoneFrame({ children, tint = "light" }) {
  return (
    <div className={`mx-auto rounded-[2rem] shadow-2xl border-4 border-neutral-900 overflow-hidden ${tint === "dark" ? "bg-neutral-900 text-white" : "bg-white"}`}
         style={{ width: 300, height: 560 }}>
      <div className="h-6 bg-neutral-900 flex items-center justify-center">
        <div className="h-1 w-16 rounded-full bg-neutral-700"/>
      </div>
      <div className="h-[calc(100%-1.5rem)] overflow-hidden">
        {children}
      </div>
    </div>
  );
}

// -------- Escenas (cada una es puramente visual, sin fetch) --------
function SceneIntro() {
  return (
    <PhoneFrame tint="dark">
      <div className="h-full flex flex-col items-center justify-center px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4 animate-pulse">
          <Bed size={28} weight="fill" className="text-red-400"/>
        </div>
        <div className="q-label text-white/40">12:14 pm</div>
        <div className="text-2xl font-light mt-1">Habitación 320</div>
        <div className="text-white/60 text-sm mt-2">A/C sin enfriar</div>
        <div className="text-white/30 text-xs mt-6">Todavía nadie lo sabe.</div>
      </div>
    </PhoneFrame>
  );
}

function SceneMaria({ progress }) {
  const step = progress < 0.33 ? 0 : progress < 0.66 ? 1 : 2;
  return (
    <PhoneFrame>
      <div className="h-full flex flex-col">
        <div className="bg-[color:var(--q-navy)] text-white px-4 py-3 text-sm flex items-center gap-2">
          <Camera size={16} weight="fill" className="text-[color:var(--q-gold)]"/>
          <span className="font-semibold">Reporte rápido</span>
        </div>
        <div className="p-4 flex-1 flex flex-col gap-3">
          <div>
            <div className="q-label mb-1 text-[color:var(--q-muted)]">1 · Ubicación</div>
            <div className="h-10 rounded-lg border border-[color:var(--q-border)] bg-neutral-50 px-3 flex items-center text-sm font-mono">
              Hab. 320
            </div>
          </div>
          <div>
            <div className="q-label mb-1 text-[color:var(--q-muted)]">2 · Foto</div>
            <div className="h-32 rounded-lg bg-neutral-200 relative overflow-hidden flex items-center justify-center">
              {step === 0 ? (
                <Camera size={40} className="text-neutral-400 animate-pulse"/>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-neutral-600 to-neutral-800 flex items-center justify-center">
                  <Bed size={40} className="text-neutral-400"/>
                  <div className="absolute bottom-1 right-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-mono">A/C</div>
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="q-label mb-1 text-[color:var(--q-muted)]">3 · Severidad</div>
            <div className="grid grid-cols-3 gap-2">
              {["leve", "media", "grave"].map((s) => (
                <div key={s} className={`h-9 rounded-lg border text-[10px] uppercase font-semibold flex items-center justify-center transition-all ${
                  step >= 1 && s === "grave"
                    ? "bg-red-500 text-white border-red-500 scale-105"
                    : "border-[color:var(--q-border)] text-[color:var(--q-muted)]"
                }`}>{s}</div>
              ))}
            </div>
          </div>
          <button className={`mt-auto h-11 rounded-lg font-semibold text-sm text-white transition-all ${
            step === 2 ? "bg-[color:var(--q-navy)] scale-105 shadow-lg" : "bg-[color:var(--q-navy)]/40"
          }`}>
            {step === 2 ? "✓ Enviado" : "Enviar reporte"}
          </button>
        </div>
      </div>
    </PhoneFrame>
  );
}

function SceneJoseValida({ progress }) {
  return (
    <PhoneFrame>
      <div className="h-full flex flex-col">
        <div className="bg-[color:var(--q-navy)] text-white px-4 py-3 text-sm flex items-center gap-2">
          <Wrench size={16} weight="fill" className="text-[color:var(--q-gold)]"/>
          <span className="font-semibold">Cola de trabajo</span>
        </div>
        <div className="p-3 flex-1 flex flex-col gap-2">
          <div className="rounded-lg border-2 border-red-500 p-3 bg-red-50">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold">Hab. 320 · A/C sin enfriar</div>
              <div className="text-[10px] px-1.5 py-0.5 rounded bg-red-500 text-white font-semibold">GRAVE</div>
            </div>
            <div className="text-[10px] text-[color:var(--q-muted)] font-mono">
              Compresor A/C · Rep US$180 · Reempl US$650
            </div>
            <div className="mt-2 grid grid-cols-3 gap-1 text-[10px]">
              <div className={`px-1.5 py-1 rounded text-center border transition-all ${progress > 0.5 ? "border-[color:var(--q-border)] text-[color:var(--q-muted)]" : "border-[color:var(--q-navy)] text-[color:var(--q-navy)] font-semibold"}`}>
                Interno
              </div>
              <div className={`px-1.5 py-1 rounded text-center border transition-all ${progress > 0.5 ? "bg-[color:var(--q-gold)] text-white border-[color:var(--q-gold)] font-semibold scale-105" : "border-[color:var(--q-border)] text-[color:var(--q-muted)]"}`}>
                Repuesto
              </div>
              <div className="px-1.5 py-1 rounded text-center border border-[color:var(--q-border)] text-[color:var(--q-muted)]">Contratista</div>
            </div>
          </div>
          <div className="rounded-lg border border-[color:var(--q-border)] p-2 text-[10px] text-[color:var(--q-muted)] bg-neutral-50">
            <div className="font-semibold text-[color:var(--q-navy)]">Otros pendientes (2)</div>
            <div>· Piscina — cloro alto</div>
            <div>· Restaurante — luz parpadeante</div>
          </div>
          <div className={`mt-auto rounded-lg p-3 text-xs text-white transition-all ${progress > 0.5 ? "bg-[color:var(--q-navy)]" : "bg-neutral-300"}`}>
            {progress > 0.5 ? "✓ Validado · requiere repuesto" : "Diagnosticando…"}
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function SceneGerencia({ progress }) {
  const approved = progress > 0.55;
  return (
    <div className="q-card p-5 max-w-md mx-auto" style={{ background: "white" }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="q-label text-[color:var(--q-muted)]">Aprobación priorizada</div>
          <div className="font-semibold">Hab. 320 · A/C sin enfriar</div>
        </div>
        <div className="text-[10px] px-2 py-0.5 rounded bg-red-500 text-white font-semibold">GRAVE</div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs mb-3">
        <div>
          <div className="q-label text-[color:var(--q-muted)]">Reparación</div>
          <div className="font-mono font-semibold">US$180</div>
        </div>
        <div>
          <div className="q-label text-[color:var(--q-muted)]">Reemplazo</div>
          <div className="font-mono font-semibold">US$650</div>
        </div>
        <div>
          <div className="q-label text-[color:var(--q-muted)]">Riesgo/día</div>
          <div className="font-mono font-semibold text-red-500">US$220</div>
        </div>
      </div>
      <div className="rounded-lg bg-[color:var(--q-gold)]/10 border border-[color:var(--q-gold)]/30 px-3 py-2 text-xs flex items-center gap-2 mb-4">
        <ArrowRight size={14} className="text-[color:var(--q-gold)]"/>
        <span className="font-semibold">Al aprobar irá a Compras</span>
        <span className="text-[color:var(--q-muted)]">· Proveedor: ClimaTec RD</span>
      </div>
      <div className="flex gap-2">
        <button className="flex-1 h-11 rounded-lg border border-[color:var(--q-border)] text-sm text-[color:var(--q-muted)]">Rechazar</button>
        <button className={`flex-1 h-11 rounded-lg font-semibold text-sm text-white transition-all ${
          approved ? "bg-emerald-600 scale-105 shadow-lg" : "bg-[color:var(--q-navy)]"
        }`}>
          {approved ? "✓ APROBADO" : "Aprobar"}
        </button>
      </div>
      <div className={`mt-3 text-center text-xs text-[color:var(--q-muted)] transition-opacity ${approved ? "opacity-100" : "opacity-0"}`}>
        Enviado a Compras · Pedro recibirá aviso ahora
      </div>
    </div>
  );
}

function ScenePedro({ progress }) {
  const step = progress < 0.35 ? 0 : progress < 0.7 ? 1 : 2;
  return (
    <div className="q-card p-5 max-w-md mx-auto" style={{ background: "white" }}>
      <div className="flex items-center gap-2 mb-3">
        <Package size={18} weight="fill" className="text-[color:var(--q-navy)]"/>
        <div className="font-semibold">Requisición #REQ-3120</div>
      </div>
      <div className="rounded-lg border border-[color:var(--q-border)] p-3 mb-3">
        <div className="text-xs text-[color:var(--q-muted)] mb-1">Compresor A/C · Hab. 320</div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="q-label text-[color:var(--q-muted)]">Proveedor sugerido</div>
            <div className="font-semibold flex items-center gap-1">
              <Truck size={12}/> ClimaTec RD
              <span className="text-[9px] px-1 rounded bg-emerald-500/20 text-emerald-700">CONTRATO</span>
            </div>
          </div>
          <div>
            <div className="q-label text-[color:var(--q-muted)]">ETA</div>
            <div className="font-mono font-semibold">3 días</div>
          </div>
          <div>
            <div className="q-label text-[color:var(--q-muted)]">Precio contrato</div>
            <div className="font-mono font-semibold">US$180</div>
          </div>
          <div>
            <div className="q-label text-[color:var(--q-muted)]">Total pedido</div>
            <div className="font-mono font-semibold text-[color:var(--q-navy)]">US$180</div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <button className={`h-11 rounded-lg border font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
          step >= 1 ? "border-[color:var(--q-gold)] bg-[color:var(--q-gold)]/10 text-[color:var(--q-navy)] scale-105" : "border-[color:var(--q-border)] text-[color:var(--q-muted)]"
        }`}>
          <DownloadSimple size={14} weight="bold"/>
          {step >= 1 ? "✓ Pedido.pdf generado" : "Generar pedido PDF"}
        </button>
        <button className={`h-11 rounded-lg font-semibold text-sm text-white transition-all ${
          step >= 2 ? "bg-emerald-600 scale-105 shadow-lg" : "bg-[color:var(--q-navy)]"
        }`}>
          {step >= 2 ? "✓ COMPRA CONFIRMADA" : "Confirmar compra"}
        </button>
      </div>
      <div className={`mt-3 text-center text-xs text-[color:var(--q-muted)] transition-opacity ${step >= 2 ? "opacity-100" : "opacity-0"}`}>
        Estado del reporte: <span className="font-semibold">ESPERANDO REPUESTO</span>
      </div>
    </div>
  );
}

function SceneEspera({ progress }) {
  const days = Math.max(0, Math.round((1 - progress) * 3));
  return (
    <div className="q-card p-8 max-w-md mx-auto text-center">
      <ChatCircleDots size={32} weight="fill" className="text-[color:var(--q-gold)] mx-auto mb-4"/>
      <div className="q-label text-[color:var(--q-muted)] mb-2">Bandeja de avisos</div>
      <div className="text-sm mb-4 text-left rounded-lg border border-[color:var(--q-border)] p-3 bg-neutral-50">
        <div className="font-semibold text-xs">Aviso a Mantenimiento</div>
        <div className="text-xs text-[color:var(--q-muted)]">Compresor A/C · Hab. 320</div>
        <div className="mt-2 text-[color:var(--q-navy)] font-mono text-sm">
          ETA: <span className="font-bold">{days}</span> {days === 1 ? "día" : "días"}
        </div>
      </div>
      <div className="text-xs text-[color:var(--q-muted)]">
        Todo esperado · todo medido · todo trazable
      </div>
    </div>
  );
}

function SceneCierre({ progress }) {
  const closed = progress > 0.55;
  return (
    <PhoneFrame>
      <div className="h-full flex flex-col">
        <div className="bg-[color:var(--q-navy)] text-white px-4 py-3 text-sm flex items-center gap-2">
          <Wrench size={16} weight="fill" className="text-[color:var(--q-gold)]"/>
          <span className="font-semibold">Ejecutando · Hab. 320</span>
        </div>
        <div className="p-4 flex-1 flex flex-col gap-3">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
            <div className="flex items-center gap-2 text-xs text-emerald-700 font-semibold mb-1">
              <Package size={12} weight="fill"/> Repuesto recibido
            </div>
            <div className="text-[10px] text-emerald-600 font-mono">Compresor A/C · ClimaTec RD</div>
          </div>
          <div className="text-xs text-[color:var(--q-muted)] space-y-1">
            <div className="flex items-center gap-2"><Check size={12} className="text-emerald-500"/> Instalado</div>
            <div className="flex items-center gap-2"><Check size={12} className="text-emerald-500"/> Probado 15 min</div>
            <div className="flex items-center gap-2"><Check size={12} className="text-emerald-500"/> Foto de cierre</div>
          </div>
          <button className={`mt-auto h-11 rounded-lg font-semibold text-sm text-white transition-all ${
            closed ? "bg-emerald-600 scale-105 shadow-lg" : "bg-[color:var(--q-navy)]"
          }`}>
            {closed ? "✓ CERRADO · Hab. de vuelta al inventario" : "Marcar como resuelto"}
          </button>
          <div className={`text-[10px] text-center text-[color:var(--q-muted)] transition-opacity ${closed ? "opacity-100" : "opacity-0"}`}>
            Aviso enviado a María: "Tu reporte fue resuelto. Gracias."
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}

function SceneFinal({ onEnter, onReplay }) {
  return (
    <div className="max-w-lg mx-auto text-center px-6">
      <div className="mx-auto w-16 h-16 rounded-full bg-[color:var(--q-gold)]/20 flex items-center justify-center mb-4">
        <Checks size={32} weight="bold" className="text-[color:var(--q-gold)]"/>
      </div>
      <h2 className="text-3xl md:text-4xl font-light text-white mb-3 leading-tight">
        Ciclo completo.<br/>
        <span className="text-[color:var(--q-gold)]">Continuidad operativa asegurada.</span>
      </h2>
      <p className="text-white/60 text-sm max-w-md mx-auto mb-8">
        Todo lo que viste sucedió en QUILLA — desde el reporte de María hasta el cierre de José — sin llamadas, sin WhatsApp perdidos, sin planillas. Cada paso auditado.
      </p>
      <div className="grid grid-cols-3 gap-3 mb-8">
        {[
          { k: "Tiempo total demo", v: "60s" },
          { k: "Pasos automatizados", v: "6" },
          { k: "Health Index recuperado", v: "82" },
        ].map((s) => (
          <div key={s.k} className="rounded-lg border border-white/10 bg-white/5 p-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{s.k}</div>
            <div className="text-white text-xl font-mono">{s.v}</div>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 justify-center">
        <button onClick={onEnter} data-testid="demo-cta-enter"
                className="h-12 px-6 rounded-lg bg-[color:var(--q-gold)] text-[color:var(--q-navy)] font-semibold flex items-center justify-center gap-2 hover:opacity-90">
          Ingresar con mi licencia <ArrowRight size={14} weight="bold"/>
        </button>
        <button onClick={onReplay} data-testid="demo-cta-replay"
                className="h-12 px-6 rounded-lg border border-white/20 text-white/80 hover:border-white/40 flex items-center justify-center gap-2">
          <Play size={14} weight="fill"/> Ver de nuevo
        </button>
      </div>
    </div>
  );
}

// -------- Componente principal --------
export default function DemoTour() {
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1 dentro de la escena
  const [paused, setPaused] = useState(false);
  const [displayHi, setDisplayHi] = useState(SCENES[0].hi_before);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const elapsedRef = useRef(0);

  const scene = SCENES[idx];
  const totalElapsed = useMemo(
    () => SCENES.slice(0, idx).reduce((a, s) => a + s.duration_ms, 0) + progress * scene.duration_ms,
    [idx, progress, scene.duration_ms]
  );

  // Reset elapsed cuando cambia escena
  useEffect(() => { elapsedRef.current = 0; setProgress(0); }, [idx]);

  // Animate Health Index suavemente durante la escena
  useEffect(() => {
    const hi = scene.hi_before + (scene.hi_after - scene.hi_before) * progress;
    setDisplayHi(Math.round(hi));
  }, [progress, scene.hi_before, scene.hi_after]);

  // Loop de animación con requestAnimationFrame
  useEffect(() => {
    if (paused) { rafRef.current && cancelAnimationFrame(rafRef.current); return; }
    const step = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = ts - lastTsRef.current;
      lastTsRef.current = ts;
      elapsedRef.current += dt;
      const p = Math.min(1, elapsedRef.current / scene.duration_ms);
      setProgress(p);
      if (p >= 1) {
        if (idx < SCENES.length - 1) {
          setIdx((v) => v + 1);
          lastTsRef.current = 0;
        } else {
          setPaused(true);
        }
      } else {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { rafRef.current && cancelAnimationFrame(rafRef.current); lastTsRef.current = 0; };
  }, [idx, paused, scene.duration_ms]);

  const goPrev = () => { if (idx > 0) setIdx(idx - 1); };
  const goNext = () => { if (idx < SCENES.length - 1) setIdx(idx + 1); else replay(); };
  const replay = () => { setIdx(0); setPaused(false); setDisplayHi(SCENES[0].hi_before); };

  const globalProgress = Math.min(1, totalElapsed / TOTAL_MS);

  return (
    <div className="min-h-screen text-white relative overflow-hidden"
         style={{ background: "radial-gradient(ellipse at top, #1F3864 0%, #0f1a30 60%, #050a17 100%)" }}
         data-testid="demo-tour">
      {/* Barra superior */}
      <div className="absolute top-0 left-0 right-0 z-20 q-safe-top">
        <div className="h-1 bg-white/10">
          <div className="h-full bg-[color:var(--q-gold)] transition-all duration-100"
               style={{ width: `${globalProgress * 100}%` }} data-testid="demo-progress"/>
        </div>
        <div className="px-4 md:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoQuilla variant="blanco" size={28}/>
            <div className="hidden sm:block h-6 w-px bg-white/20"/>
            <div className="text-xs text-white/50 hidden sm:block">Demo guiado · Ocean El Faro</div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPaused((p) => !p)} data-testid="demo-toggle-pause"
                    className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center" title={paused ? "Reanudar" : "Pausar"}>
              {paused ? <Play size={16} weight="fill"/> : <Pause size={16} weight="fill"/>}
            </button>
            <button onClick={goPrev} disabled={idx === 0} data-testid="demo-prev"
                    className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center disabled:opacity-30" title="Anterior">
              <ArrowLeft size={14} weight="bold"/>
            </button>
            <button onClick={goNext} data-testid="demo-next"
                    className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center" title="Siguiente">
              <SkipForward size={16} weight="fill"/>
            </button>
            <button onClick={() => nav("/login")} data-testid="demo-exit"
                    className="h-9 px-3 ml-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs flex items-center gap-1">
              <XIcon size={14}/> Salir
            </button>
          </div>
        </div>
      </div>

      {/* Contenido central */}
      <div className="pt-20 pb-32 px-4 md:px-8 min-h-screen flex flex-col justify-center">
        <div className="grid md:grid-cols-[1fr_320px] gap-8 max-w-6xl mx-auto w-full items-center">
          <div className="flex flex-col items-center">
            {scene.id === "intro"        && <SceneIntro/>}
            {scene.id === "maria"        && <SceneMaria progress={progress}/>}
            {scene.id === "jose_valida"  && <SceneJoseValida progress={progress}/>}
            {scene.id === "gerencia"     && <SceneGerencia progress={progress}/>}
            {scene.id === "pedro"        && <ScenePedro progress={progress}/>}
            {scene.id === "espera"       && <SceneEspera progress={progress}/>}
            {scene.id === "cierre"       && <SceneCierre progress={progress}/>}
            {scene.id === "final"        && <SceneFinal onEnter={() => nav("/login")} onReplay={replay}/>}
          </div>

          {/* Panel lateral fijo con Health Index + rol activo */}
          {scene.id !== "final" && (
            <div className="hidden md:flex flex-col gap-4">
              <div className="q-label text-white/40">Rol actuando</div>
              <div className="text-2xl font-light text-white" data-testid="demo-role">{scene.role}</div>
              <HealthBar score={displayHi}/>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="q-label text-white/40 mb-2">Escena {idx + 1} / {SCENES.length}</div>
                <div className="text-sm text-white/80 leading-relaxed">{scene.caption}</div>
              </div>
            </div>
          )}
        </div>

        {/* En mobile, caption abajo */}
        {scene.id !== "final" && (
          <div className="md:hidden mt-6 max-w-xl mx-auto w-full">
            <HealthBar score={displayHi}/>
            <div className="mt-3 text-center">
              <div className="q-label text-white/40">{scene.role}</div>
              <div className="text-sm text-white/80 mt-1">{scene.caption}</div>
            </div>
          </div>
        )}
      </div>

      {/* Footer sutil */}
      <div className="absolute bottom-3 left-0 right-0 text-center">
        <div className="text-[10px] uppercase tracking-widest text-white/30 font-mono">
          QUILLA · Continuidad Operativa como Servicio · v1
        </div>
      </div>
    </div>
  );
}
